import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  TableContainer,
} from '@mui/material';
import {
  AddOutlined,
  VisibilityOutlined,
  SearchOutlined,
  PictureAsPdfOutlined,
  ReceiptOutlined,
  PaidOutlined,
  PendingOutlined,
  CalendarTodayOutlined,
  PersonOutlined,
  DescriptionOutlined,
} from '@mui/icons-material';
import { useFactureStore } from '../stores/useFactureStore';
import { useClientStore } from '../stores/useClientStore';
import { useQuoteStore } from '../stores/useQuoteStore';
import { StatutFacture, TypeFacture } from '../types';
import { formatEuro, formatDate } from '../utils/calculations';

const FactureListPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { factures, loadFactures } = useFactureStore();
  const { getClientById } = useClientStore();
  const { getDevisById } = useQuoteStore();
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadFactures();
  }, [loadFactures]);

  const getStatutColor = (statut: StatutFacture) => {
    switch (statut) {
      case StatutFacture.EN_ATTENTE:
        return 'warning';
      case StatutFacture.PAYEE:
        return 'success';
      case StatutFacture.ANNULEE:
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: TypeFacture) => {
    switch (type) {
      case TypeFacture.ACOMPTE:
        return 'Acompte';
      case TypeFacture.SOLDE:
        return 'Solde';
      case TypeFacture.COMPLETE:
        return 'Complète';
      default:
        return type;
    }
  };

  const filteredFactures = factures.filter((facture) => {
    const client = getClientById(facture.clientId);
    const devis = getDevisById(facture.devisId);

    const searchLower = searchQuery.toLowerCase();

    // Utiliser clientNom si disponible, sinon chercher dans le store
    const clientName = facture.clientNom || (client ? `${client.prenom} ${client.nom}` : '');

    return (
      facture.numero.toLowerCase().includes(searchLower) ||
      clientName.toLowerCase().includes(searchLower) ||
      devis?.numero.toLowerCase().includes(searchLower)
    );
  });

  // Calcul des statistiques
  const stats = {
    total: factures.length,
    enAttente: factures.filter((f) => f.statut === StatutFacture.EN_ATTENTE).length,
    payees: factures.filter((f) => f.statut === StatutFacture.PAYEE).length,
    montantTotal: factures
      .filter((f) => f.statut === StatutFacture.PAYEE)
      .reduce((sum, f) => sum + f.montantTTC, 0),
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#333' }}>
          Factures
        </Typography>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total factures
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {stats.total}
                  </Typography>
                </Box>
                <ReceiptOutlined sx={{ fontSize: 48, color: 'theme.palette.primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    En attente
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {stats.enAttente}
                  </Typography>
                </Box>
                <PendingOutlined sx={{ fontSize: 48, color: '#ff9800', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Payées
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
                    {stats.payees}
                  </Typography>
                </Box>
                <PaidOutlined sx={{ fontSize: 48, color: '#4CAF50', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Montant payé
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, fontSize: '1.8rem' }}>
                    {formatEuro(stats.montantTotal)}
                  </Typography>
                </Box>
                <PaidOutlined sx={{ fontSize: 48, color: '#4CAF50', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barre de recherche */}
      <Paper sx={{ p: 3, mb: 3, boxShadow: 2, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Rechercher par numéro, client ou devis..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Liste des factures */}
      {isMobile ? (
        // Vue mobile: Cartes
        <Stack spacing={2}>
          {filteredFactures.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', boxShadow: 2, borderRadius: 2 }}>
              <Typography variant="body1" color="text.secondary">
                {searchQuery ? 'Aucune facture trouvée' : 'Aucune facture'}
              </Typography>
            </Paper>
          ) : (
            filteredFactures.map((facture) => {
              const client = getClientById(facture.clientId);
              const devis = getDevisById(facture.devisId);

              return (
                <Card
                  key={facture.id}
                  sx={{
                    boxShadow: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 },
                  }}
                  onClick={() => navigate(`/factures/${facture.id}`)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'theme.palette.primary.main' }}>
                          {facture.numero}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getTypeLabel(facture.type)}
                          {facture.type === TypeFacture.ACOMPTE && facture.pourcentageAcompte && ` (${facture.pourcentageAcompte}%)`}
                        </Typography>
                      </Box>
                      <Chip
                        label={facture.statut.replace('_', ' ')}
                        color={getStatutColor(facture.statut)}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {facture.clientNom || (client ? `${client.prenom} ${client.nom}` : 'Client inconnu')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Devis: {devis?.numero || '-'}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Émission: {formatDate(facture.dateEmission)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          Échéance: {formatDate(facture.dateEcheance)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                        {formatEuro(facture.montantTTC)}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{ color: 'theme.palette.primary.main' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/factures/${facture.id}`);
                        }}
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
      ) : (
        // Vue desktop: Tableau
        <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Numéro</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Devis</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date émission</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date échéance</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Montant TTC</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFactures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchQuery ? 'Aucune facture trouvée' : 'Aucune facture'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredFactures.map((facture) => {
                  const client = getClientById(facture.clientId);
                  const devis = getDevisById(facture.devisId);

                  return (
                    <TableRow
                      key={facture.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f9f9f9' },
                      }}
                      onClick={() => navigate(`/factures/${facture.id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {facture.numero}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getTypeLabel(facture.type)}
                          {facture.type === TypeFacture.ACOMPTE && facture.pourcentageAcompte && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              ({facture.pourcentageAcompte}%)
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {facture.clientNom || (client ? `${client.prenom} ${client.nom}` : 'Client inconnu')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {devis?.numero || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(facture.dateEmission)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(facture.dateEcheance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatEuro(facture.montantTTC)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={facture.statut.replace('_', ' ')}
                          color={getStatutColor(facture.statut)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Voir les détails">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/factures/${facture.id}`)}
                            sx={{ color: 'theme.palette.primary.main' }}
                          >
                            <VisibilityOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default FactureListPage;
